import React from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { LogOut, PanelLeft, HelpCircle } from 'lucide-react';
import CreditMeter from '../signature/CreditMeter';
import ThemeToggle from '../ThemeToggle';

const ChatHeader = ({
  sidebarCollapsed, setSidebarCollapsed, agents, selectedAgent, selectedAgentId,
  onAgentChange, tokenInfo, currentConversation, user, onLogout, onProfile, onShowUsageGuide,
}) => (
  <header
    className="atelier-glass-nav px-3 md:px-6 py-3 flex-shrink-0 relative z-20"
    data-testid="chat-header"
  >
    <div className="flex items-center justify-between gap-3">
      {/* ── Left cluster ── */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {sidebarCollapsed && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost" size="sm" onClick={() => setSidebarCollapsed(false)}
                  className="rounded-full h-8 w-8 p-0 flex-shrink-0 text-atelier-mute hover:text-atelier-ink hover:bg-atelier-surface2 border border-atelier-line hover:border-atelier-lineHi transition-all duration-300"
                  data-testid="show-sidebar-btn"
                >
                  <PanelLeft className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">顯示側邊欄</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Kicker — editorial label */}
        <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-atelier-coral atelier-breathe" />
          <span className="atelier-caption">Chat · Studio</span>
          <span className="h-3.5 w-px bg-atelier-line" />
        </div>

        {/* Agent selector — pill, 1px hairline */}
        <div className="flex-shrink-0">
          <Select value={selectedAgentId} onValueChange={onAgentChange}>
            <SelectTrigger
              className="rounded-full h-9 w-auto min-w-[150px] md:min-w-[240px] text-sm bg-atelier-surface border border-atelier-line hover:border-atelier-coral/50 text-atelier-ink transition-all duration-300 px-3.5 focus:ring-0 focus:ring-offset-0"
              data-testid="agent-selector"
            >
              <SelectValue>
                {selectedAgent ? (
                  <div className="flex items-center gap-2">
                    <span className="text-base leading-none">{selectedAgent.icon}</span>
                    <span className="hidden sm:inline truncate text-atelier-ink" style={{ fontWeight: 500 }}>{selectedAgent.name}</span>
                  </div>
                ) : (
                  <span className="text-atelier-subtle">選擇 Agent</span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-atelier-surface border border-atelier-line rounded-xl">
              {agents.map((agent) => (
                <SelectItem
                  key={agent.id} value={agent.id}
                  className="text-atelier-ink hover:!bg-atelier-surface2 focus:!bg-atelier-surface2 data-[state=checked]:!bg-atelier-coral/10 data-[state=checked]:!text-atelier-coral rounded-lg text-sm my-0.5 transition-colors"
                >
                  <div className="flex items-center gap-2.5 py-0.5">
                    <span className="text-xl leading-none">{agent.icon}</span>
                    <span className="font-medium">{agent.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedAgent?.usage_guide && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onShowUsageGuide}
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-atelier-subtle hover:text-atelier-coral hover:bg-atelier-surface2 transition-all duration-300"
                  data-testid="usage-guide-btn"
                >
                  <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">使用說明</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Token capacity meter — editorial hairline */}
        {tokenInfo && currentConversation && (
          <div className="hidden md:flex items-center gap-2.5 ml-1">
            <span className="atelier-caption">Capacity</span>
            <div className="w-24 h-px bg-atelier-line relative overflow-hidden rounded-full">
              <div
                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out ${
                  tokenInfo.approaching_limit ? 'bg-red-500' : 'bg-atelier-coral'
                }`}
                style={{
                  width: `${Math.min((tokenInfo.current / tokenInfo.max) * 100, 100)}%`,
                  height: '2px',
                  top: '-0.5px',
                }}
              />
            </div>
            <span className="atelier-caption tabular-nums" style={{ letterSpacing: '0.12em' }}>
              {Math.round((tokenInfo.current / tokenInfo.max) * 100)}%
            </span>
          </div>
        )}
      </div>

      {/* ── Right cluster ── */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <CreditMeter current={user?.credits ?? 0} onClick={onProfile} />

        <Button
          variant="ghost" size="sm" onClick={onLogout}
          className="rounded-full text-atelier-subtle hover:text-red-500 hover:bg-red-500/10 h-9 w-9 p-0 transition-all duration-300"
          data-testid="logout-btn"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} />
        </Button>

        <ThemeToggle />
      </div>
    </div>
  </header>
);

export default ChatHeader;
